import type { Point, PointArray } from '../types/contour-definition-array-type';

import { vec2 } from 'gl-matrix';

import { EPSILON } from '@/src/constants';

// todo define this in the constants file
const subMatrix = 4;

/**
 * Checks if two points are equal.
 */
export function isEqualPoint(p1: Point, p2: Point): boolean {
  return p1.x === p2.x && p1.y === p2.y;
}

/**
 * Aligns all points to a 4x4 grid per unit (grid size = 0.25).
 */
export function alignPointsToGrid(
  points: PointArray,
  resolution: number = subMatrix,
): PointArray {
  return points.map(p => ({
    x: Math.round(p.x * resolution) / resolution,
    y: Math.round(p.y * resolution) / resolution,
  }));
}

/**
 * Trims each point’s x/y to lie within [0, width] and [0, height].
 */
export function removePointsOutsideArea(
  points: Point[],
  width: number,
  height: number,
): Point[] {
  return points.map(p => ({
    x: Math.max(0, Math.min(p.x, width)),
    y: Math.max(0, Math.min(p.y, height)),
  }));
}

/**
 * Generates a Bresenham line between two integer grid points.
 * Coordinates are in integer grid space (units of 0.25 in original).
 */
function bresenhamLine(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;

  while (true) {
    pts.push({ x, y });
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
  }
  return pts;
}

/**
 * Interpolates a path of grid-aligned points using Bresenham.
 * If `closed` is true, also interpolates the closing segment
 * from the last point back to the first.
 */
export function interpolatePoints(
  points: PointArray,
  closed: boolean = false,
  resolution: number = subMatrix,
): PointArray {
  // Deduplicate
  const seen = new Set<string>();
  const unique: Point[] = [];

  for (const p of points) {
    const key = `${p.x},${p.y}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(p);
    }
  }

  const len = unique.length;
  if (len < 2) return [...unique];

  const interpolatedInt: { x: number; y: number }[] = [];
  const segmentsCount = closed ? len : len - 1;

  for (let i = 0; i < segmentsCount; i++) {
    const a = unique[i];
    const b = unique[(i + 1) % len];
    const x0 = Math.round(a.x * resolution);
    const y0 = Math.round(a.y * resolution);
    const x1 = Math.round(b.x * resolution);
    const y1 = Math.round(b.y * resolution);
    const segment = bresenhamLine(x0, y0, x1, y1);

    // Include full first segment; skip the start point for subsequent to avoid duplicates
    const startIdx = i === 0 ? 0 : 1;
    for (let j = startIdx; j < segment.length; j++) {
      interpolatedInt.push(segment[j]);
    }
  }

  // Map back to the input space
  return interpolatedInt.map(p => ({
    x: p.x / resolution,
    y: p.y / resolution,
  }));
}

export function smoothPoints(
  points: PointArray,
  closed: boolean = false,
  resolution: number = subMatrix,
): PointArray {
  if (points.length < 3) return [...points];
  const outputPolyLine = [...points];
  if (closed) {
    const first = outputPolyLine[0];
    const last = outputPolyLine[outputPolyLine.length - 1];
    if (first.x === last.x && first.y === last.y) {
      outputPolyLine.pop();
    }
  }
  const v1 = vec2.create();
  const v2 = vec2.create();
  const v3 = vec2.create();

  // Remove right-angle corners
  for (let k = 2; k < outputPolyLine.length; k++) {
    const p1 = outputPolyLine[k - 2];
    const p2 = outputPolyLine[k - 1];
    const p3 = outputPolyLine[k];
    vec2.set(v1, p1.x, p1.y);
    vec2.set(v2, p2.x, p2.y);
    vec2.set(v3, p3.x, p3.y);
    vec2.sub(v1, v1, v2);
    vec2.sub(v2, v2, v3);
    if (Math.abs(vec2.dot(v1, v2)) < EPSILON) {
      outputPolyLine.splice(k - 1, 1);
      k--;
    }
  }

  if (closed) {
    const first = outputPolyLine[0];
    outputPolyLine.push({ x: first.x, y: first.y });
  }

  const step = 1 / resolution;
  const result: Point[] = [];
  for (let i = 0; i < outputPolyLine.length; i++) {
    const p = outputPolyLine[i];
    const last = result[result.length - 1];
    if (last && last.x === p.x && last.y === p.y) {
      continue;
    }
    if (last) {
      const dx = Math.abs(p.x - last.x);
      const dy = Math.abs(p.y - last.y);
      if (dx > step || dy > step) {
        const interpolated = interpolatePoints([last, p], false, resolution);
        for (let j = 1; j < interpolated.length; j++) {
          result.push(interpolated[j]);
        }

        continue;
      }
    }
    result.push(p);
  }

  return result;
}

/**
 * Checks if segments p1->p2 and p3->p4 intersect.
 */
function segmentsIntersect(
  p1: Point,
  p2: Point,
  p3: Point,
  p4: Point,
): boolean {
  const orient = (a: Point, b: Point, c: Point) => {
    const v = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
    return Math.sign(v);
  };
  const o1 = orient(p1, p2, p3);
  const o2 = orient(p1, p2, p4);
  const o3 = orient(p3, p4, p1);
  const o4 = orient(p3, p4, p2);
  return o1 !== o2 && o3 !== o4;
}

/**
 * Removes loops by:
 * 1) Iteratively stripping out smaller loops caused by segment crossings.
 * 2) Then removing duplicate-point loops.
 */
export function removeLoops(
  points: PointArray,
  closed: boolean = false,
): PointArray {
  // Copy points and unwrap closing duplicate if present
  const pts = [...points];
  if (closed && pts.length > 1) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (first.x === last.x && first.y === last.y) {
      pts.pop();
    }
  }

  let changed = true;

  // Remove crossing loops
  while (changed) {
    changed = false;
    for (let i = 0; i < pts.length - 1; i++) {
      for (let j = i + 2; j < pts.length - 1; j++) {
        if (i === 0 && j === pts.length - 2) continue;
        if (segmentsIntersect(pts[i], pts[i + 1], pts[j], pts[j + 1])) {
          const lenA = j - i;
          const lenB = pts.length - lenA;
          if (lenA <= lenB) {
            pts.splice(i + 1, lenA);
          }
          else {
            pts.splice(j + 1, lenB - 1);
            pts.splice(0, i + 1);
          }
          changed = true;
          break;
        }
      }
      if (changed) break;
    }
  }

  // Remove duplicate-point loops
  const result: Point[] = [];
  const seen = new Set<string>();

  for (const p of pts) {
    const key = `${p.x},${p.y}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(p);
    }
  }

  // Restore closure if needed
  if (closed && result.length > 0) {
    result.push({ x: result[0].x, y: result[0].y });
  }

  return result;
}

/**
 * Checks for duplicate points and validates that each consecutive segment
 * lies within a 0.25×0.25 neighbourhood. For closed polygons, also tests
 * the closing segment and ignores the expected duplicate of first/last point.
 */
export function validatePoints(
  points: PointArray,
  closed: boolean = false,
  resolution: number = subMatrix,
): boolean {
  const seen = new Map<string, number>();
  const duplicateIndices: number[] = [];
  const n = points.length;

  // Detect duplicates (ignore closing duplicate if expected)
  points.forEach((p, idx) => {
    const key = `${p.x},${p.y}`;
    if (seen.has(key)) {
      if (
        !(closed && idx === n - 1 && key === `${points[0].x},${points[0].y}`)
      ) {
        duplicateIndices.push(idx);
      }
    }
    else {
      seen.set(key, idx);
    }
  });

  // Check consecutive segment distances
  const invalidSegments: { index: number; dx: number; dy: number }[] = [];
  const maxStep = 1 / resolution;
  for (let i = 1; i < n; i++) {
    const dx = Math.abs(points[i].x - points[i - 1].x);
    const dy = Math.abs(points[i].y - points[i - 1].y);
    if (dx > maxStep || dy > maxStep) {
      invalidSegments.push({ index: i - 1, dx, dy });
    }
  }

  // Closing segment strict match check
  let closingMismatch = false;
  if (closed && n > 1) {
    const first = points[0];
    const last = points[n - 1];
    if (first.x !== last.x || first.y !== last.y) {
      closingMismatch = true;
      const dx = Math.abs(last.x - first.x);
      const dy = Math.abs(last.y - first.y);
      invalidSegments.push({ index: n - 1, dx, dy });
    }
  }

  const hasDuplicates = duplicateIndices.length > 0;
  const allConsecutiveValid = invalidSegments.length === 0;
  const hasClosingMismatch = closingMismatch;

  const isValid = !hasDuplicates && allConsecutiveValid && !hasClosingMismatch;

  return isValid;
}

/**
 * Full pipeline: align to grid, interpolate, then remove loops.
 */
export function processPoints(
  points: PointArray,
  isClosed: boolean,
  width?: number,
  height?: number,
  skipInsideCheck: boolean = false,
  resolution: number = subMatrix,
): PointArray {
  const aligned = alignPointsToGrid(points, resolution);
  const inside
    = !skipInsideCheck && width !== undefined && height !== undefined
      ? removePointsOutsideArea(aligned, width, height)
      : aligned;
  const interpolated = interpolatePoints(inside, isClosed, resolution);
  const smoothed = smoothPoints(interpolated, isClosed, resolution);
  const removedLoops = removeLoops(smoothed, isClosed);
  const isValid = validatePoints(removedLoops, isClosed, resolution);
  if (!isValid) {
    return [];
  }
  return removedLoops;
}
